'use client';
/**
 * components/FiltersSheet.tsx — OGRYN
 *
 * Bottom sheet de filtres prestataires (OGRYN) :
 *   1. Ville : autocomplete geo.api.gouv.fr + bouton GPS
 *   2. Distance max (slider)
 *   3. Métier : autocomplete skills.json
 *   4. Score min / VIP only
 *   5. Sauvegarde de filtres (localStorage via contexte)
 *
 * ⚠️  Aucun import Leaflet.
 */

import { useState, useEffect, useRef, useCallback, type CSSProperties } from 'react';
import { useMobile } from '@/contexts/MobileContext';

const TAB_COLOR = '#2E7D52'; // OGRYN = prestataires only

// ─── Skills autocomplete ───────────────────────────────────────────────────────

interface SkillNode { label: string; skills?: string[]; subcategories?: { label: string; skills: string[] }[] }

function useSkillsFlat(): string[] {
  const [skills, setSkills] = useState<string[]>([]);
  useEffect(() => {
    fetch('/data/skills.json')
      .then(r => r.json())
      .then((data: SkillNode[]) => {
        const flat: string[] = [];
        for (const cat of data) {
          if (cat.skills) flat.push(...cat.skills);
          for (const sub of cat.subcategories ?? []) flat.push(...sub.skills);
        }
        setSkills([...new Set(flat)]);
      })
      .catch(() => {});
  }, []);
  return skills;
}

// ─── Autocomplete ville (API gouvernement) ────────────────────────────────────

function useCitySuggestions(query: string): string[] {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (query.length < 2) { setSuggestions([]); return; } // eslint-disable-line react-hooks/set-state-in-effect
    timerRef.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(query)}&fields=nom&limit=6&boost=population`,
        );
        const data = await r.json() as { nom: string }[];
        setSuggestions(data.map(d => d.nom));
      } catch { setSuggestions([]); }
    }, 250);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [query]);

  return suggestions;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function FiltersSheet() {
  const {
    filtersOpen, setFiltersOpen,
    filters,     updateFilter, resetFilters,
    savedFilters, saveCurrentFilters, applySavedFilter, deleteSavedFilter, setDefaultFilter,
  } = useMobile();

  const allSkills   = useSkillsFlat();
  const [villeInput,  setVilleInput]  = useState(filters.ville);
  const [metierInput, setMetierInput] = useState(filters.metier);
  const [villeSuggs,  setVilleSuggs]  = useState<string[]>([]);
  const [metierSuggs, setMetierSuggs] = useState<string[]>([]);
  const [saveMode,    setSaveMode]    = useState(false);
  const [saveName,    setSaveName]    = useState('');
  const [locLoading,  setLocLoading]  = useState(false);
  const [geoErr,      setGeoErr]      = useState('');
  const [showSaved,   setShowSaved]   = useState(false);

  const citySuggestions = useCitySuggestions(villeInput);

  useEffect(() => { setVilleInput(filters.ville); },  [filters.ville]);
  useEffect(() => { setMetierInput(filters.metier); }, [filters.metier]);
  useEffect(() => { setVilleSuggs(citySuggestions); }, [citySuggestions]);

  useEffect(() => {
    if (metierInput.length < 2) { setMetierSuggs([]); return; }
    const q = metierInput.toLowerCase();
    setMetierSuggs(allSkills.filter(s => s.toLowerCase().includes(q)).slice(0, 7));
  }, [metierInput, allSkills]);

  const selectVille = useCallback((nom: string) => {
    setVilleInput(nom);
    updateFilter('ville', nom);
    setVilleSuggs([]);
  }, [updateFilter]);

  const selectMetier = useCallback((s: string) => {
    setMetierInput(s);
    updateFilter('metier', s);
    setMetierSuggs([]);
  }, [updateFilter]);

  const geolocate = useCallback(() => {
    if (!navigator.geolocation) { setGeoErr('GPS non disponible'); return; }
    setLocLoading(true);
    setGeoErr('');
    navigator.geolocation.getCurrentPosition(
      async pos => {
        try {
          const r = await fetch(
            `https://geo.api.gouv.fr/communes?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&fields=nom&limit=1`,
          );
          const data = await r.json() as { nom: string }[];
          if (data[0]) selectVille(data[0].nom);
        } catch { /* ignore */ }
        setLocLoading(false);
      },
      (err) => {
        setLocLoading(false);
        if (err.code === 1) setGeoErr('Permission GPS refusée');
        else setGeoErr('Position indisponible');
      },
      { timeout: 8000, enableHighAccuracy: true },
    );
  }, [selectVille]);

  const handleSave = useCallback(() => {
    saveCurrentFilters(saveName);
    setSaveMode(false);
    setSaveName('');
    setShowSaved(true);
  }, [saveCurrentFilters, saveName]);

  if (!filtersOpen) return null;

  const tabColor = TAB_COLOR;

  const inputStyle: CSSProperties = {
    width: '100%', height: 44,
    background: 'rgba(255,255,255,.06)',
    border: '0.5px solid rgba(255,255,255,.12)',
    borderRadius: 10, padding: '0 12px',
    color: 'rgba(255,255,255,.88)',
    /* font-size ≥ 16px : empêche le zoom iOS Safari */
    fontSize: 16, outline: 'none',
    boxSizing: 'border-box',
  };

  const sectionLabel: CSSProperties = {
    fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.35)',
    textTransform: 'uppercase', letterSpacing: '.07em',
    marginBottom: 8, display: 'block',
  };

  return (
    <>
      {/* Backdrop — ne couvre pas la nav */}
      <div
        onClick={() => setFiltersOpen(false)}
        style={{ position: 'fixed', inset: 0, bottom: '56px', zIndex: 695 }}
      />

      {/* Wrapper positionné — au-dessus des FABs */}
      <div style={{
        position: 'fixed',
        bottom:   'calc(116px + env(safe-area-inset-bottom))',
        left:     12,
        right:    12,
        zIndex:   850,
      }}>

      {/* ── Bulle — flex colonne, pas de scroll ── */}
      <div
        style={{
          background: 'rgba(10,6,3,.98)',
          borderRadius: 18,
          border: '0.5px solid rgba(255,255,255,.10)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >

        <div style={{ padding: '10px 16px' }}>

          {/* ── VILLE + GPS ── */}
          <div style={{ marginBottom: 16, position: 'relative' }}>
            <span style={sectionLabel}>Localisation</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  style={inputStyle}
                  value={villeInput}
                  onChange={e => setVilleInput(e.target.value)}
                  onBlur={() => { updateFilter('ville', villeInput); setTimeout(() => setVilleSuggs([]), 200); }}
                  placeholder="Ville…"
                />
                {villeSuggs.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: 'rgba(14,9,4,.98)', border: '0.5px solid rgba(255,255,255,.12)', borderRadius: 10, marginTop: 4, overflow: 'hidden' }}>
                    {villeSuggs.map(v => (
                      <div key={v} onMouseDown={() => selectVille(v)}
                        style={{ padding: '11px 14px', fontSize: 15, color: 'rgba(255,255,255,.82)', cursor: 'pointer', borderBottom: '0.5px solid rgba(255,255,255,.05)' }}>
                        {v}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" onClick={geolocate} disabled={locLoading}
                style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, background: 'rgba(255,255,255,.06)', border: `0.5px solid ${tabColor}55`, color: locLoading ? 'rgba(255,255,255,.25)' : tabColor, cursor: locLoading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {locLoading ? (
                  <span style={{ fontSize: 14 }}>…</span>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {geoErr && (
              <div style={{ marginTop: 4, fontSize: 10, color: '#fca5a5', fontWeight: 600 }}>
                {geoErr}
              </div>
            )}
          </div>

          {/* ── DISTANCE ── */}
          <div style={{ marginBottom: 16 }}>
            <span style={sectionLabel}>
              Rayon — <span style={{ color: tabColor, fontWeight: 900 }}>{filters.distanceKm} km</span>
            </span>
            <input type="range" min={1} max={100} step={1}
              value={filters.distanceKm}
              onChange={e => updateFilter('distanceKm', Number(e.target.value))}
              style={{ width: '100%', accentColor: tabColor }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,255,255,.28)', marginTop: 2 }}>
              <span>1 km</span><span>100 km</span>
            </div>
          </div>

          {/* ── MÉTIER / COMPÉTENCE ── */}
          <div style={{ marginBottom: 16, position: 'relative' }}>
            <span style={sectionLabel}>Métier / Compétence</span>
            <input
              style={inputStyle}
              value={metierInput}
              onChange={e => setMetierInput(e.target.value)}
              onBlur={() => { updateFilter('metier', metierInput); setTimeout(() => setMetierSuggs([]), 200); }}
              placeholder="Plombier, IRVE, charpente…"
            />
            {metierSuggs.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: 'rgba(14,9,4,.98)', border: '0.5px solid rgba(255,255,255,.12)', borderRadius: 10, marginTop: 4, overflow: 'hidden' }}>
                {metierSuggs.map(s => (
                  <div key={s} onMouseDown={() => selectMetier(s)}
                    style={{ padding: '11px 14px', fontSize: 15, color: 'rgba(255,255,255,.82)', cursor: 'pointer', borderBottom: '0.5px solid rgba(255,255,255,.05)' }}>
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── SCORE MIN ── */}
          <div style={{ marginBottom: 14 }}>
            <span style={sectionLabel}>
              Score minimum — <span style={{ color: tabColor, fontWeight: 900 }}>{filters.scoreMin === 0 ? 'Tous' : filters.scoreMin}</span>
            </span>
            <input type="range" min={0} max={100} step={5}
              value={filters.scoreMin}
              onChange={e => updateFilter('scoreMin', Number(e.target.value))}
              style={{ width: '100%', accentColor: tabColor }} />
          </div>

          {/* ── VIP ONLY ── */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, cursor: 'pointer' }}>
            <div style={{ width: 40, height: 24, borderRadius: 12, position: 'relative', flexShrink: 0, transition: 'background .15s', background: filters.vipOnly ? '#F59E0B' : 'rgba(255,255,255,.12)', cursor: 'pointer' }}
              onClick={() => updateFilter('vipOnly', !filters.vipOnly)}>
              <div style={{ position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .15s', left: filters.vipOnly ? 19 : 3 }} />
            </div>
            <span style={{ fontSize: 14, color: filters.vipOnly ? '#F59E0B' : 'rgba(255,255,255,.5)', fontWeight: 600 }}>
              Prestataires VIP uniquement
            </span>
          </label>

          {/* ── FILTRES SAUVEGARDÉS ── */}
          <div style={{ borderTop: '0.5px solid rgba(255,255,255,.07)', paddingTop: 14, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: '.07em' }}>
                Filtres sauvegardés
              </span>
              {savedFilters.length > 0 && (
                <button type="button" onClick={() => setShowSaved(s => !s)}
                  style={{ background: 'none', border: 'none', color: tabColor, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  {showSaved ? 'Masquer' : `Voir (${savedFilters.length})`}
                </button>
              )}
            </div>

            {showSaved && savedFilters.map((sf, i) => (
              <div key={sf.savedAt} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'rgba(255,255,255,.04)', borderRadius: 10, border: `0.5px solid ${sf.isDefault ? tabColor + '55' : 'rgba(255,255,255,.08)'}`, marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,.8)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    {sf.isDefault && <span style={{ color: '#F59E0B', display: 'inline-flex' }}><svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg></span>}
                    {sf.name}
                  </div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,.3)', marginTop: 1 }}>
                    {[sf.filters.ville, sf.filters.metier].filter(Boolean).join(' · ') || 'Tous types'}
                  </div>
                </div>
                <button type="button" onClick={() => { applySavedFilter(i); setFiltersOpen(false); }}
                  style={{ fontSize: 10, color: tabColor, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px' }}>
                  Appliquer
                </button>
                <button type="button" onClick={() => setDefaultFilter(i)} aria-label="Définir par défaut"
                  style={{ display: 'inline-flex', background: 'none', border: 'none', cursor: 'pointer', opacity: sf.isDefault ? 1 : 0.3, color: '#F59E0B', padding: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                </button>
                <button type="button" onClick={() => deleteSavedFilter(i)}
                  style={{ fontSize: 14, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,.3)' }}>
                  ✕
                </button>
              </div>
            ))}

            {!saveMode ? (
              <button type="button" onClick={() => setSaveMode(true)}
                style={{ width: '100%', height: 40, borderRadius: 10, background: 'rgba(255,255,255,.05)', border: `0.5px solid ${tabColor}44`, color: tabColor, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginBottom: 4 }}>
                Sauvegarder cette recherche
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                <input value={saveName} onChange={e => setSaveName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
                  placeholder="Nom de la recherche…"
                  style={{ ...inputStyle, flex: 1, height: 40, fontSize: 14 }}
                   
                  autoFocus
                />
                <button type="button" onClick={handleSave}
                  style={{ height: 40, padding: '0 14px', borderRadius: 10, background: tabColor, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  OK
                </button>
                <button type="button" onClick={() => setSaveMode(false)}
                  style={{ height: 40, width: 40, borderRadius: 10, background: 'rgba(255,255,255,.06)', border: 'none', color: 'rgba(255,255,255,.5)', fontSize: 14, cursor: 'pointer' }}>
                  ✕
                </button>
              </div>
            )}
          </div>

          {/* ── FOOTER ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14, borderTop: '0.5px solid rgba(255,255,255,.07)' }}>
            <button type="button"
              onClick={() => { resetFilters(); setVilleInput(''); setMetierInput(''); }}
              title="Réinitialiser les filtres"
              style={{ background: 'rgba(255,255,255,.05)', border: '0.5px solid rgba(255,255,255,.12)', borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.45)', cursor: 'pointer' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
              </svg>
            </button>
            <button type="button" onClick={() => setFiltersOpen(false)}
              style={{ background: tabColor, border: 'none', borderRadius: 10, padding: '10px 22px', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Fermer
            </button>
          </div>
        </div>
      </div>{/* fin bulle */}

      {/* Triangle pointant vers le FAB loupe (centre) */}
      <div style={{ position: 'absolute', bottom: -9, left: 'calc(50% - 9px)', width: 0, height: 0, borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderTop: '9px solid rgba(255,255,255,.10)' }} />
      <div style={{ position: 'absolute', bottom: -8, left: 'calc(50% - 8px)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid rgba(10,6,3,.98)' }} />

      </div>{/* fin wrapper */}
    </>
  );
}
